import { Entry, EntryCollection } from 'contentful';
import axios from 'axios';

import {
  ContentfulArticle,
  ContentfulSection,
  SectionTree,
  SectionTypeTree,
} from 'types/common';
import { makeSectionTree, groupSectionsByType } from 'services/pages/common';
import { AvailabilityResponse } from 'types/apiHelpers';
import {
  API_VERSION,
  AVAILABILITY_URL,
  BUTTON_IDS,
  DEPLOYMENT_ID,
  ORG_ID,
  SALESFORCE_API_ENDPOINT,
} from 'constants/salesforce';
import {
  BR_DTS_ENABLED_ARTICLES,
  DTS_ENABLED_ARTICLES,
  EU_DTS_ENABLED_COUNTRIES,
} from 'constants/common';
import logger from 'services/logger';

export const getArticleIdFromPageSlug: (dataObject: {
  [key: string]: string | string[];
}) => string = (dataObject) => {
  const articleSlug = dataObject && dataObject.articleSlug;

  if (typeof articleSlug === 'string') {
    return articleSlug ? articleSlug.split('-')[0] : '';
  }
  return '';
};

interface ContentfulRes extends EntryCollection<ContentfulArticle['fields']> {
  includes?: {
    Entry: ContentfulSection[];
  };
}

export const makeDataTreeForArticlesPage = ({
  contentfulRes,
  localizedSectionsRes,
}: {
  contentfulRes: ContentfulRes;
  localizedSectionsRes: EntryCollection<ContentfulSection['fields']>;
}): { sectionTree: SectionTree; sectionTypeTree: SectionTypeTree } => {
  const sections = (contentfulRes?.includes?.Entry || []).filter(
    (item) => item?.sys?.contentType?.sys?.id === 'section',
  );
  const localizedSections = (localizedSectionsRes?.items || []).map(
    (item) => item?.sys?.id,
  );
  const sectionTree = makeSectionTree({
    sections,
    contentfulRes,
    localizedSections,
  });
  const sectionTypeTree = groupSectionsByType({
    sectionTree,
    localizedSections,
  });

  return { sectionTree, sectionTypeTree };
};

type MapArticlesToISGPaths = (
  contentfulArticles: Entry<ContentfulArticle['fields']>[],
  locales: string[],
) => {
  params: {
    articleSlug: string;
  };
  locale: string;
}[];

export const mapArticlesToISGPaths: MapArticlesToISGPaths = (
  contentfulArticles,
  locales,
) =>
  contentfulArticles &&
  contentfulArticles
    .filter((article) => article?.fields?.slug)
    .reduce(
      (
        acc: {
          params: {
            articleSlug: string;
          };
          locale: string;
        }[],
        currentArticle,
      ) => {
        const localisedArticles = locales
          .filter((locale) => !!currentArticle?.fields?.slug[locale])
          .map((locale) => ({
            params: {
              articleSlug: `${currentArticle?.sys?.id || ''}-${
                currentArticle?.fields?.slug[locale]
              }`,
            },
            locale,
          }));
        return [...acc, ...localisedArticles];
      },
      [],
    );

export const httpSFAvailabilityCheck = async ({
  env,
  locale,
}: {
  env: string;
  locale: string;
}): Promise<boolean> => {
  let chatAvailable = false;

  try {
    const availabilityResponse: AvailabilityResponse = await axios.get(
      `${SALESFORCE_API_ENDPOINT[env]}${AVAILABILITY_URL}`,
      {
        headers: {
          'X-LIVEAGENT-API-VERSION': `${API_VERSION}`,
        },
        params: {
          'org_id': ORG_ID[env] as string,
          'deployment_id': DEPLOYMENT_ID,
          'Availability.ids': BUTTON_IDS[locale] as string,
          'Availability.needEstimatedWaitTime': 1,
        },
      },
    );
    if (
      availabilityResponse?.data?.messages[0]?.type === 'Availability' &&
      availabilityResponse?.data?.messages[0]?.message.results[0].isAvailable
    ) {
      chatAvailable = true;
    } else {
      logger.warn('[Support Centre Live Agent]: No agents are available');
    }
  } catch (err) {
    logger.error(
      err as Error,
      'Static generation - articlesPage - chatAvailability',
    );
  }
  return chatAvailable;
};

export const shouldShowDTSBanner = (
  articleId: string,
  locale: string,
): boolean =>
  (BR_DTS_ENABLED_ARTICLES.includes(articleId) && locale === 'pt-BR') ||
  (DTS_ENABLED_ARTICLES.includes(articleId) &&
    EU_DTS_ENABLED_COUNTRIES.includes(locale));
